"use client";

import { c as _c } from "react/compiler-runtime";
import useCreateModalState from "@/hooks/useCreateModalState";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createBreakpoint } from "react-use";
import type { ApiDrop } from "../../generated/models/ApiDrop";
import { DropSize } from "../../helpers/waves/drop.helpers";
import { useSidebarState } from "../../hooks/useSidebarState";
import { commonApiFetch } from "../../services/api/common-api";
import { useAuth } from "../auth/Auth";
import BrainDesktopDrop from "../brain/BrainDesktopDrop";
import WebBrainLeftSidebar from "../brain/left-sidebar/web/WebLeftSidebar";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import BrainRightSidebar, { SidebarTab } from "../brain/right-sidebar/BrainRightSidebar";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import CreateWaveModal from "../waves/create-wave/CreateWaveModal";
import { WaveChatScrollProvider } from "@/contexts/wave/WaveChatScrollContext";
const useBreakpoint = createBreakpoint({
  XL: 1400,
  LG: 1024,
  S: 0
});
interface WavesMessagesWrapperProps {
  readonly children: ReactNode;
  readonly defaultPath?: string | undefined; // "/waves" or "/messages"
  readonly showLeftSidebar?: boolean | undefined;
}
const WavesMessagesWrapper: React.FC<WavesMessagesWrapperProps> = t0 => {
  const $ = _c(53);
  const {
    children,
    defaultPath: t1,
    showLeftSidebar: t2
  } = t0;
  const defaultPath = t1 === undefined ? "/waves" : t1;
  const showLeftSidebar = t2 === undefined ? true : t2;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const {
    contentContainerStyle
  } = useLayout();
  const {
    isRightSidebarOpen,
    closeRightSidebar
  } = useSidebarState();
  const {
    connectedProfile
  } = useAuth();
  const {
    isWaveModalOpen,
    close
  } = useCreateModalState();
  const [sidebarTab, setSidebarTab] = useState(SidebarTab.ABOUT);
  let t3;
  let waveId;
  if ($[0] !== searchParams) {
    const rawDropId = searchParams?.get("drop") ?? undefined;
    waveId = searchParams?.get("wave") ?? undefined;
    t3 = rawDropId && /^[a-zA-Z0-9-_]+$/.test(rawDropId) ? rawDropId : undefined;
    $[0] = searchParams;
    $[1] = t3;
    $[2] = waveId;
  } else {
    t3 = $[1];
    waveId = $[2];
  }
  const dropId = t3;
  const isMobile = breakpoint === "S";
  const isLargeDesktop = breakpoint === "XL";
  let t4;
  let t5;
  if ($[3] !== closeRightSidebar || $[4] !== isRightSidebarOpen || $[5] !== waveId) {
    t4 = () => {
      if (!waveId && isRightSidebarOpen) {
        closeRightSidebar();
      }
    };
    t5 = [waveId, isRightSidebarOpen, closeRightSidebar];
    $[3] = closeRightSidebar;
    $[4] = isRightSidebarOpen;
    $[5] = waveId;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== dropId) {
    t6 = [QueryKey.DROP, {
      drop_id: dropId
    }];
    t7 = async () => await commonApiFetch({
      endpoint: `drops/${dropId}`
    });
    $[8] = dropId;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  const t8 = !!dropId;
  let t9;
  if ($[11] !== t6 || $[12] !== t7 || $[13] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      placeholderData: keepPreviousData,
      enabled: t8
    };
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const {
    data: drop,
    error: dropError
  } = useQuery(t9);
  let t10;
  if ($[15] !== defaultPath || $[16] !== pathname || $[17] !== router || $[18] !== searchParams) {
    t10 = () => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("drop");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || defaultPath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[15] = defaultPath;
    $[16] = pathname;
    $[17] = router;
    $[18] = searchParams;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const onDropClose = t10;
  drop?.id;
  let t11;
  if ($[20] !== drop?.id || $[21] !== dropId) {
    t11 = dropId && drop?.id?.toLowerCase() === dropId?.toLowerCase();
    $[20] = drop?.id;
    $[21] = dropId;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const isDropOpen = Boolean(t11);
  const shouldShowLeftSidebar = showLeftSidebar && (!isMobile || !waveId);
  const shouldShowMainContent = !isMobile || waveId;
  const shouldShowDropOverlay = isDropOpen && drop && shouldShowMainContent;
  const shouldShowRightSidebar = Boolean(isRightSidebarOpen && waveId && !isDropOpen);
  const canInlineRight = !isMobile && (isLargeDesktop || breakpoint === "LG");
  let rightVariant = null;
  if (shouldShowRightSidebar) {
    rightVariant = canInlineRight ? "inline" : "overlay";
  }
  if (dropError && dropId) {
    console.error("Failed to load drop:", dropError);
  }
  let t12;
  if ($[23] !== rightVariant || $[24] !== shouldShowLeftSidebar) {
    t12 = shouldShowLeftSidebar && <WebBrainLeftSidebar isCollapsed={rightVariant === "inline"} />;
    $[23] = rightVariant;
    $[24] = shouldShowLeftSidebar;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== children || $[27] !== drop || $[28] !== onDropClose || $[29] !== shouldShowDropOverlay || $[30] !== shouldShowMainContent) {
    t13 = shouldShowMainContent && <div className="tw-flex tw-h-full tw-min-w-0 tw-flex-grow tw-flex-col tw-border-y-0 tw-border-l-0 tw-border-r tw-border-solid tw-border-iron-800">{children}{shouldShowDropOverlay && <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[1010] lg:tw-absolute lg:tw-inset-0 lg:tw-z-[1010]"><BrainDesktopDrop drop={{
          type: DropSize.FULL,
          ...drop,
          stableKey: drop.id,
          stableHash: drop.id
        }} onClose={onDropClose} /></div>}</div>;
    $[26] = children;
    $[27] = drop;
    $[28] = onDropClose;
    $[29] = shouldShowDropOverlay;
    $[30] = shouldShowMainContent;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== rightVariant || $[33] !== sidebarTab || $[34] !== waveId) {
    t14 = rightVariant === "inline" && <div className="tw-hidden tw-flex-shrink-0 tw-pl-6 tw-pt-2 lg:tw-block"><BrainRightSidebar variant="inline" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} /></div>;
    $[32] = rightVariant;
    $[33] = sidebarTab;
    $[34] = waveId;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== contentContainerStyle || $[37] !== t12 || $[38] !== t13 || $[39] !== t14) {
    t15 = <div className="tw-relative tw-flex tw-flex-col"><div className="tw-relative tw-flex tw-flex-grow"><div className="tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-full tw-flex-grow"><div className="tw-relative tw-flex tw-w-full tw-overflow-hidden" style={contentContainerStyle}>{t12}{t13}{t14}</div></div></div></div>;
    $[36] = contentContainerStyle;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== rightVariant || $[42] !== sidebarTab || $[43] !== waveId) {
    t16 = rightVariant === "overlay" && <BrainRightSidebar variant="overlay" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} />;
    $[41] = rightVariant;
    $[42] = sidebarTab;
    $[43] = waveId;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== close || $[46] !== connectedProfile || $[47] !== isWaveModalOpen) {
    t17 = connectedProfile && <CreateWaveModal isOpen={isWaveModalOpen} onClose={close} profile={connectedProfile} />;
    $[45] = close;
    $[46] = connectedProfile;
    $[47] = isWaveModalOpen;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t15 || $[50] !== t16 || $[51] !== t17) {
    t18 = <WaveChatScrollProvider>{t15}{t16}{t17}</WaveChatScrollProvider>;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
};
export default WavesMessagesWrapper;